import { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { FormField, FormInput, FormLabel } from './FormFields';
import { IconPicker } from './IconPicker';
import { TagIcon } from './TagIcon';
import type { Tag } from '@poi-planner/shared';
import clsx from 'clsx';
import { DEFAULT_PHOSPHOR_ICON, toPhosphorTagIcon } from '../utils/tagIcons';

// Preset colors for tag markers
const TAG_COLORS = [
  { hex: '#3b82f6', label: 'Blue' },
  { hex: '#22c55e', label: 'Green' },
  { hex: '#f59e0b', label: 'Amber' },
  { hex: '#ef4444', label: 'Red' },
  { hex: '#8b5cf6', label: 'Purple' },
  { hex: '#ec4899', label: 'Pink' },
  { hex: '#14b8a6', label: 'Teal' },
  { hex: '#f97316', label: 'Orange' },
];

interface TagEditorModalProps {
  open: boolean;
  onClose: () => void;
  tag?: Tag; // If provided, we're editing; otherwise creating
  onSave: (data: Omit<Tag, 'id'>) => Promise<void>;
  onDelete?: () => Promise<void>;
  usageCount?: number;
}

export function TagEditorModal({
  open,
  onClose,
  tag,
  onSave,
  onDelete,
  usageCount = 0,
}: TagEditorModalProps) {
  const isEditing = !!tag;
  const defaultIconValue = toPhosphorTagIcon(DEFAULT_PHOSPHOR_ICON);

  const [name, setName] = useState('');
  const [icon, setIcon] = useState(defaultIconValue);
  const [color, setColor] = useState<string | undefined>(undefined);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/tag changes
  useEffect(() => {
    if (open) {
      setName(tag?.name ?? '');
      setIcon(tag?.icon ?? defaultIconValue);
      setColor(tag?.color);
      setShowColorPicker(!!tag?.color);
      setShowDeleteConfirm(false);
      setError(null);
    }
  }, [open, tag, defaultIconValue]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave({
        name: name.trim(),
        icon,
        color: showColorPicker ? color : undefined,
      });
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Tag' : 'New Tag'}
      icon="label"
      width="520px"
      footer={
        <div className="flex items-center justify-between w-full">
          <div>
            {isEditing && onDelete && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={saving}
                className="btn-danger"
              >
                Delete Tag
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !name.trim()}
              className="btn-primary px-5 py-2 text-sm disabled:opacity-50"
            >
              {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Tag'}
            </button>
          </div>
        </div>
      }
    >
      {/* Delete confirmation overlay */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 bg-white/95 backdrop-blur-sm z-10 flex flex-col items-center justify-center p-6">
          <div className="flex items-center justify-center size-16 rounded-full bg-red-100 mb-4">
            <span className="material-symbols-outlined text-3xl text-danger">
              delete_forever
            </span>
          </div>
          <h3 className="text-lg font-bold text-text-primary mb-2">
            Delete "{tag?.name}"?
          </h3>
          <p className="text-sm text-text-secondary text-center mb-6 max-w-xs">
            {usageCount > 0
              ? `This tag is used by ${usageCount} ${usageCount === 1 ? 'POI' : 'POIs'}. Deleting it will remove the tag from all POIs.`
              : 'This action cannot be undone.'}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={saving}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-danger text-white cursor-pointer border-none transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Deleting...' : 'Delete Tag'}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-5 relative">
        {/* Error message */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-danger">
            <span className="material-symbols-outlined text-lg">error</span>
            {error}
          </div>
        )}

        {/* Preview */}
        <div className="flex items-center gap-4 p-4 bg-input-bg rounded-xl">
          <div
            className="flex items-center justify-center size-14 rounded-xl transition-colors"
            style={{ backgroundColor: showColorPicker && color ? color : '#111418' }}
          >
            <TagIcon icon={icon} size={30} whiteTint />
          </div>
          <div className="flex-1">
            <div className="text-sm font-bold text-text-primary">
              {name || 'Tag Name'}
            </div>
            <div className="text-xs text-text-secondary mt-0.5">
              Preview of your tag icon
            </div>
          </div>
          {isEditing && usageCount > 0 && (
            <div className="px-2.5 py-1 bg-white rounded-lg text-xs font-semibold text-text-secondary border border-border">
              {usageCount} {usageCount === 1 ? 'POI' : 'POIs'}
            </div>
          )}
        </div>

        {/* Name input */}
        <FormField label="Name">
          <FormInput
            value={name}
            onChange={setName}
            placeholder="e.g., Restaurant, Museum, Park"
          />
        </FormField>

        {/* Icon picker */}
        <div>
          <FormLabel>Icon</FormLabel>
          <IconPicker
            value={icon}
            onChange={setIcon}
            color={showColorPicker ? color : undefined}
          />
        </div>

        {/* Color picker */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <FormLabel className="!mb-0">Marker Color</FormLabel>
            <button
              type="button"
              onClick={() => setShowColorPicker(!showColorPicker)}
              className={clsx(
                'relative w-9 h-5 rounded-full border-none cursor-pointer transition-colors p-0',
                showColorPicker ? 'bg-text-primary' : 'bg-border'
              )}
            >
              <div
                className={clsx(
                  'absolute top-0.5 size-4 rounded-full bg-white transition-[left]',
                  showColorPicker ? 'left-[18px]' : 'left-0.5'
                )}
              />
            </button>
          </div>

          {showColorPicker && (
            <div className="flex gap-2 mt-2">
              {TAG_COLORS.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  onClick={() => setColor(c.hex)}
                  title={c.label}
                  className={clsx(
                    'size-9 rounded-lg cursor-pointer transition-all hover:scale-110 border-2',
                    color === c.hex
                      ? 'ring-2 ring-offset-2 ring-text-primary'
                      : 'border-transparent'
                  )}
                  style={{ backgroundColor: c.hex }}
                />
              ))}
            </div>
          )}
          {!showColorPicker && (
            <p className="text-xs text-text-muted mt-1">
              Uses default dark marker. Enable to customize color.
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
